import { Colors } from "@jsq/ds/styles";
import { LogLevel } from "~/hooks/useDataStudioQueries/constants";

export interface ParsedLogEntry {
  level: string;
  timestamp: string;
  message: string;
}

const isValidLogLevel = (value: string): value is LogLevel => {
  return Object.values(LogLevel).some((enumValue) => enumValue === value);
};

/**
 * Parse log entry into level, timestamp, and message
 */
export const parseLogEntry = (logEntry: {
  timestamp: string;
  message: string;
}): ParsedLogEntry => {
  let message = logEntry.message;

  // Remove the prefix pattern: [timestamp] {{filename.py:line}} LEVEL -
  // Example: [2025-10-08T17:42:14.479+0000] {{task_log_fetcher.py:63}} INFO -
  // Pattern matches: [date/time] {{file.py:line}} LEVEL -
  const prefixPattern =
    /^\[[^\]]+\]\s+\{\{[^}]+\}\}\s+(INFO|ERROR|WARNING|DEBUG|WARN|CRITICAL)\s+-\s*/;
  const prefixMatch = message.match(prefixPattern);

  let level = LogLevel.INFO;
  if (prefixMatch && isValidLogLevel(prefixMatch[1])) {
    level = prefixMatch[1];
    message = message.slice(prefixMatch[0].length);
  } else {
    // Fallback: try to extract log level from start of message
    const logLevelMatch = message.match(/^(INFO|ERROR|WARNING|DEBUG|WARN|CRITICAL)\s*-?\s*/);
    if (logLevelMatch && isValidLogLevel(logLevelMatch[1])) {
      level = logLevelMatch[1];
      message = message.slice(logLevelMatch[0].length);
    }
  }

  const timestampMs = parseInt(logEntry.timestamp, 10);
  const date = new Date(timestampMs);
  const timestamp = date.toLocaleString("en-US", {
    hour12: false,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    timeZone: "UTC",
  });

  return {
    level,
    timestamp,
    message: message.trim(),
  };
};

/**
 * Get color for log level
 */
export const getLogLevelColor = (level: string): string => {
  switch (level.toUpperCase()) {
    case LogLevel.ERROR:
    case LogLevel.CRITICAL:
      return Colors.Danger400;
    case LogLevel.WARNING:
    case LogLevel.WARN:
      return Colors.Warning400;
    case LogLevel.DEBUG:
      return Colors.Neutral400;
    case LogLevel.INFO:
    default:
      return Colors.Success400;
  }
};

/**
 * Get CSS class name for log level
 */
export const getLogLevelClassName = (level: string): string => {
  const upperLevel = level.toUpperCase();

  if (upperLevel === LogLevel.ERROR || upperLevel === LogLevel.CRITICAL) {
    return "logLevelError";
  }
  if (upperLevel === LogLevel.WARNING || upperLevel === LogLevel.WARN) {
    return "logLevelWarning";
  }
  if (upperLevel === LogLevel.DEBUG) {
    return "logLevelDebug";
  }
  return "logLevelInfo";
};
